import React, { useState } from "react";
import { useMonyawn } from "../../../context/MonyawnContext";
import { ProofType } from "../../../types";

const proofTypeLabel: Record<ProofType, string> = {
  resume: "Resume",
  job_description: "Job Description",
  message: "Message",
  note: "Note",
  offer: "Offer",
  debrief: "Debrief",
  generated_output: "Generated Output",
  sow: "SOW",
  proposal: "Proposal",
  rate_sheet: "Rate Sheet",
  other: "Other",
};

const proofTypeDot: Record<ProofType, string> = {
  resume: "bg-blue-400",
  job_description: "bg-purple-400",
  message: "bg-green-400",
  note: "bg-yellow-400",
  offer: "bg-emerald-400",
  debrief: "bg-orange-400",
  generated_output: "bg-pink-400",
  sow: "bg-indigo-400",
  proposal: "bg-cyan-400",
  rate_sheet: "bg-teal-400",
  other: "bg-gray-400",
};

type Props = {
  proofId: string;
};

export const ProofCard: React.FC<Props> = ({ proofId }) => {
  const { state, patchState } = useMonyawn();
  const proof = state.proof.find((p) => p.proof_id === proofId);
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editLabel, setEditLabel] = useState("");
  const [editNote, setEditNote] = useState("");
  const [editSummary, setEditSummary] = useState("");

  if (!proof) return null;

  function startEdit() {
    setEditLabel(proof!.source_label);
    setEditNote(proof!.evidence_note);
    setEditSummary(proof!.content_summary);
    setEditing(true);
    setExpanded(true);
  }

  function saveEdit() {
    patchState(
      (current) => ({
        ...current,
        proof: current.proof.map((p) =>
          p.proof_id === proofId
            ? {
                ...p,
                source_label: editLabel,
                evidence_note: editNote,
                content_summary: editSummary,
                review_status: "user_reviewed",
              }
            : p,
        ),
      }),
      "Proof updated.",
    );
    setEditing(false);
  }

  function archiveProof() {
    patchState(
      (current) => ({
        ...current,
        proof: current.proof.map((p) =>
          p.proof_id === proofId ? { ...p, review_status: "archived" } : p,
        ),
      }),
      "Proof archived.",
    );
  }

  const isArchived = proof.review_status === "archived";

  return (
    <div
      className={`rounded-2xl border transition-all ${
        isArchived
          ? "bg-black/3 border-black/5 opacity-50"
          : "bg-white border-black/8 shadow-sm hover:shadow-md"
      }`}
    >
      {/* Collapsed header — always visible */}
      <div className="flex items-center gap-3 px-4 py-3">
        <span className={`w-2.5 h-2.5 rounded-full flex-none ${proofTypeDot[proof.proof_type]}`} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-brand-ink truncate">{proof.source_label}</p>
          <p className="text-xs text-brand-muted">
            {proofTypeLabel[proof.proof_type]} · v{proof.version_number}
          </p>
        </div>
        <span
          className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${
            proof.review_status === "approved"
              ? "bg-green-50 text-green-600"
              : proof.review_status === "user_reviewed"
                ? "bg-blue-50 text-blue-600"
                : proof.review_status === "archived"
                  ? "bg-gray-50 text-gray-400"
                  : "bg-yellow-50 text-yellow-600"
          }`}
        >
          {proof.review_status.replace("_", " ")}
        </span>
        <button
          onClick={() => setExpanded((v) => !v)}
          className="text-brand-muted hover:text-brand-ink transition-colors ml-1"
          aria-label={expanded ? "Collapse" : "Expand"}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path
              d={expanded ? "M4 10l4-4 4 4" : "M4 6l4 4 4-4"}
              stroke="currentColor"
              strokeWidth="1.5"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      {/* Expanded body */}
      {expanded && (
        <div className="px-4 pb-4 border-t border-black/5 pt-3 space-y-3">
          {editing ? (
            <>
              <label className="flex flex-col gap-1">
                <span className="text-[10px] font-bold uppercase tracking-widest text-brand-muted">
                  Label
                </span>
                <input
                  className="w-full px-3 py-2 rounded-xl border border-black/10 bg-brand-surface text-sm focus:ring-2 focus:ring-brand-accent outline-none"
                  value={editLabel}
                  onChange={(e) => setEditLabel(e.target.value)}
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-[10px] font-bold uppercase tracking-widest text-brand-muted">
                  Why it matters
                </span>
                <input
                  className="w-full px-3 py-2 rounded-xl border border-black/10 bg-brand-surface text-sm focus:ring-2 focus:ring-brand-accent outline-none"
                  value={editNote}
                  onChange={(e) => setEditNote(e.target.value)}
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-[10px] font-bold uppercase tracking-widest text-brand-muted">
                  Summary
                </span>
                <textarea
                  className="w-full px-3 py-2 rounded-xl border border-black/10 bg-brand-surface text-sm focus:ring-2 focus:ring-brand-accent outline-none resize-none"
                  rows={4}
                  value={editSummary}
                  onChange={(e) => setEditSummary(e.target.value)}
                />
              </label>
              <div className="flex gap-2">
                <button
                  onClick={saveEdit}
                  className="bg-brand-accent text-white px-4 py-1.5 rounded-xl text-xs font-bold hover:bg-brand-accent-strong transition-all"
                >
                  Save
                </button>
                <button
                  onClick={() => setEditing(false)}
                  className="bg-brand-surface border border-black/10 text-brand-ink px-4 py-1.5 rounded-xl text-xs font-bold hover:bg-black/5 transition-all"
                >
                  Cancel
                </button>
              </div>
            </>
          ) : (
            <>
              {proof.evidence_note && (
                <p className="text-xs text-brand-muted">{proof.evidence_note}</p>
              )}
              {proof.content_summary && (
                <p className="text-sm text-brand-ink leading-relaxed whitespace-pre-wrap">
                  {proof.content_summary}
                </p>
              )}
              <div className="flex gap-2 pt-1">
                <button
                  onClick={startEdit}
                  className="bg-brand-surface border border-black/10 text-brand-ink px-3 py-1.5 rounded-xl text-xs font-bold hover:bg-black/5 transition-all"
                >
                  Edit
                </button>
                {!isArchived && (
                  <button
                    onClick={archiveProof}
                    className="text-brand-muted px-3 py-1.5 rounded-xl text-xs hover:bg-black/5 transition-all"
                  >
                    Archive
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};
